import { Children, ComponentType, ReactElement, ReactNode, isValidElement } from "react";

import {
  SelectDividerProps,
  SelectGroupProps,
  SelectItem,
  SelectOption,
  SelectOptionGroup,
  SelectOptionProps,
  isOptionGroup,
  isSelectOption
} from "./Select";

export interface SelectComponents {
  SelectDividerComponent: ComponentType<SelectDividerProps>;
  SelectGroupComponent: ComponentType<SelectGroupProps>;
  SelectOptionComponent: ComponentType<SelectOptionProps>;
}

export const flattenOptions = (items: SelectItem[]): SelectOption[] => {
  const flattened: SelectOption[] = [];
  items.forEach((item) => {
    if (isSelectOption(item)) {
      flattened.push(item);
      return;
    }

    if (isOptionGroup(item)) {
      flattened.push(...item.options);
    }
  });
  return flattened;
};

export const parseChildren = ({ children, components }: { children: ReactNode; components: SelectComponents }): SelectItem[] => {
  const items: SelectItem[] = [];

  Children.forEach(children, (child) => {
    if (!isValidElement(child)) {
      return;
    }

    const element = child as ReactElement;

    if (element.type === components.SelectOptionComponent) {
      const props = element.props as SelectOptionProps;
      items.push({
        label: typeof props.children === "string" ? props.children : String(props.children),
        value: props.value
      });
      return;
    }

    if (element.type === components.SelectGroupComponent) {
      const props = element.props as SelectGroupProps;
      const groupOptions: SelectOption[] = [];

      Children.forEach(props.children, (groupChild) => {
        if (!isValidElement(groupChild)) {
          return;
        }

        const groupElement = groupChild as ReactElement;

        if (groupElement.type === components.SelectOptionComponent) {
          const optionProps = groupElement.props as SelectOptionProps;
          groupOptions.push({
            label: typeof optionProps.children === "string" ? optionProps.children : String(optionProps.children),
            value: optionProps.value
          });
        }
      });

      const groupItem: SelectOptionGroup = {
        type: "group",
        label: props.label,
        options: groupOptions
      };

      items.push(groupItem);
      return;
    }

    if (element.type === components.SelectDividerComponent) {
      const props = element.props as SelectDividerProps;
      items.push({
        type: "divider",
        label: props.label
      });
    }
  });

  return items;
};

export const getItems = ({
  children,
  components,
  options
}: {
  children?: ReactNode;
  components: SelectComponents;
  options?: SelectItem[];
}): SelectItem[] => {
  if (children) {
    return parseChildren({ children, components });
  }

  return (options || []).map((opt) => ({ ...opt }));
};
